import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
  Package, Plus, Edit3, Trash2, CheckCircle2, X, ChevronDown, ChevronRight, Save, AlertTriangle
} from 'lucide-react';
import { superApi } from './SuperAdminAuth';

const SuperPlansPage = () => {
  const [data, setData] = useState({ plans: [], modules: [], integrations: [] });
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);  // plan doc being edited, null = closed
  const [creating, setCreating] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await superApi.get('/super/plans');
      setData(r.data);
    } catch (e) {
      toast.error('Failed to load plans');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const closeEditor = (changed = false) => {
    setEditing(null);
    setCreating(false);
    if (changed) load();
  };

  if (loading) return <div className="p-8 text-slate-400">Loading plans…</div>;

  return (
    <div className="p-6 md:p-8 max-w-7xl mx-auto" data-testid="super-plans">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2">
          <Package className="w-6 h-6 text-amber-400" />
          <h1 className="text-2xl font-semibold text-white">Subscription Plans</h1>
        </div>
        <button
          onClick={() => { setCreating(true); setEditing(newPlanTemplate()); }}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-amber-500 hover:bg-amber-400 text-black text-sm font-medium"
          data-testid="create-plan-btn"
        >
          <Plus className="w-4 h-4" /> New plan
        </button>
      </div>

      <p className="text-xs text-slate-500 mb-4">
        Each plan defines its price, usage limits, features shown on the pricing page, and which app modules it unlocks.
        Changes take effect immediately — the manual <code className="font-mono bg-slate-900 px-1 rounded">/onboard</code> page and the Shopify App Store billing flow both read from this DB.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
        {data.plans.map(p => (
          <PlanCard key={p.id} plan={p} onEdit={() => setEditing(p)} onDelete={load} />
        ))}
      </div>

      {editing && (
        <PlanEditor
          plan={editing}
          creating={creating}
          registry={data.modules}
          integrationsRegistry={data.integrations || []}
          onClose={closeEditor}
        />
      )}
    </div>
  );
};

const newPlanTemplate = () => ({
  id: '',
  name: '',
  price: 0,
  interval: 'month',
  orders_limit: 0,
  leads_limit: 0,
  users_limit: 0,
  extra_order_price: 0,
  extra_user_price: 10,
  features: [],
  modules_enabled: ['dashboard', 'orders'],
  integrations: [],
  stripe_price_id: '',
  shopify_plan_name: '',
  is_active: true,
  is_default: false,
  sort_order: 100,
  description: '',
});

const PlanCard = ({ plan, onEdit, onDelete }) => {
  const [deleting, setDeleting] = useState(false);
  const handleDelete = async () => {
    if (!window.confirm(`Delete plan "${plan.name}"? Only possible if no company is on it.`)) return;
    setDeleting(true);
    try {
      await superApi.delete(`/super/plans/${plan.id}`);
      toast.success(`Deleted "${plan.name}"`);
      onDelete();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    } finally { setDeleting(false); }
  };
  return (
    <div className={`bg-slate-900 border ${plan.is_active ? 'border-slate-800' : 'border-slate-800 opacity-60'} rounded-xl p-5 flex flex-col`} data-testid={`plan-card-${plan.id}`}>
      <div className="flex items-start justify-between">
        <div>
          <h3 className="text-lg font-semibold text-white">{plan.name}</h3>
          <p className="text-xs text-slate-500 font-mono mt-0.5">{plan.id}</p>
        </div>
        <span className={`text-xs px-2 py-0.5 rounded ${plan.is_active ? 'bg-emerald-500/10 text-emerald-300' : 'bg-slate-700 text-slate-400'}`}>
          {plan.is_active ? 'active' : 'inactive'}
        </span>
      </div>
      <div className="mt-3">
        <span className="text-3xl font-bold text-white">${plan.price}</span>
        <span className="text-sm text-slate-400">/{plan.interval}</span>
      </div>
      <p className="text-xs text-slate-500 mt-1">{plan.description || '—'}</p>
      <dl className="mt-4 space-y-1 text-xs">
        <Row label="Orders" value={plan.orders_limit === -1 ? 'Unlimited' : plan.orders_limit.toLocaleString()} />
        <Row label="Leads" value={plan.leads_limit === -1 ? 'Unlimited' : plan.leads_limit.toLocaleString()} />
        <Row label="Users" value={plan.users_limit} />
        <Row label="Modules" value={plan.modules_enabled?.length || 0} />
        <Row label="Features" value={plan.features?.length || 0} />
      </dl>
      <div className="flex-1" />
      <div className="mt-4 flex gap-2">
        <button onClick={onEdit} className="flex-1 inline-flex items-center justify-center gap-1 py-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-xs text-slate-200" data-testid={`edit-plan-${plan.id}`}>
          <Edit3 className="w-3 h-3" /> Edit
        </button>
        {plan.id !== 'free' && (
          <button onClick={handleDelete} disabled={deleting} className="px-3 py-2 rounded-lg bg-slate-800 hover:bg-rose-500/20 text-rose-400 text-xs disabled:opacity-50">
            <Trash2 className="w-3 h-3" />
          </button>
        )}
      </div>
    </div>
  );
};

const Row = ({ label, value }) => (
  <div className="flex items-center justify-between text-slate-400">
    <dt>{label}</dt><dd className="text-slate-200 font-mono">{value}</dd>
  </div>
);

// --- Editor modal --------------------------------------------------------
const PlanEditor = ({ plan, creating, registry, integrationsRegistry = [], onClose }) => {
  const [form, setForm] = useState(plan);
  const [saving, setSaving] = useState(false);
  const [showJson, setShowJson] = useState(false);

  const up = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const moduleGroups = useMemo(() => {
    const map = {};
    for (const m of registry) { (map[m.group] = map[m.group] || []).push(m); }
    return map;
  }, [registry]);

  const integrationGroups = useMemo(() => {
    const map = {};
    for (const i of integrationsRegistry) { (map[i.group] = map[i.group] || []).push(i); }
    return map;
  }, [integrationsRegistry]);

  const toggleModule = (key) => {
    const enabled = new Set(form.modules_enabled);
    enabled.has(key) ? enabled.delete(key) : enabled.add(key);
    up('modules_enabled', [...enabled]);
  };

  const toggleIntegration = (key) => {
    const enabled = new Set(form.integrations || []);
    enabled.has(key) ? enabled.delete(key) : enabled.add(key);
    up('integrations', [...enabled]);
  };

  const addFeature = () => up('features', [...(form.features || []), '']);
  const updateFeature = (i, v) => {
    const fs = [...form.features]; fs[i] = v; up('features', fs);
  };
  const removeFeature = (i) => up('features', form.features.filter((_, idx) => idx !== i));

  const save = async () => {
    if (!form.id || !form.name) { toast.error('ID and Name are required'); return; }
    setSaving(true);
    try {
      if (creating) {
        await superApi.post('/super/plans', form);
        toast.success(`Plan "${form.name}" created`);
      } else {
        await superApi.put(`/super/plans/${form.id}`, form);
        toast.success(`Plan "${form.name}" updated`);
      }
      onClose(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={() => onClose(false)}>
      <div className="bg-slate-900 border border-slate-800 rounded-xl max-w-3xl w-full max-h-[90vh] overflow-auto" onClick={e => e.stopPropagation()} data-testid="plan-editor">
        <header className="px-6 py-4 border-b border-slate-800 flex items-center justify-between sticky top-0 bg-slate-900 z-10">
          <h2 className="text-lg font-semibold text-white">{creating ? 'New Plan' : `Edit ${form.name}`}</h2>
          <button onClick={() => onClose(false)} className="text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>
        </header>

        <div className="p-6 space-y-5">
          <div className="grid grid-cols-2 gap-3">
            <Field label="ID (slug)" hint="lowercase, a-z 0-9 _ -">
              <input value={form.id} onChange={e => up('id', e.target.value)} disabled={!creating}
                     className={inputCls} placeholder="pro" data-testid="plan-id" />
            </Field>
            <Field label="Display Name">
              <input value={form.name} onChange={e => up('name', e.target.value)} className={inputCls} placeholder="Pro" data-testid="plan-name" />
            </Field>
          </div>

          <Field label="Description">
            <input value={form.description || ''} onChange={e => up('description', e.target.value)} className={inputCls} placeholder="For scaling operations" />
          </Field>

          <div className="grid grid-cols-3 gap-3">
            <Field label="Price (USD)">
              <input type="number" step="0.01" value={form.price} onChange={e => up('price', parseFloat(e.target.value) || 0)} className={inputCls} data-testid="plan-price" />
            </Field>
            <Field label="Interval">
              <select value={form.interval} onChange={e => up('interval', e.target.value)} className={inputCls}>
                <option value="month">Month</option>
                <option value="year">Year</option>
              </select>
            </Field>
            <Field label="Sort Order">
              <input type="number" value={form.sort_order} onChange={e => up('sort_order', parseInt(e.target.value) || 0)} className={inputCls} />
            </Field>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <Field label="Orders limit" hint="-1 = unlimited">
              <input type="number" value={form.orders_limit} onChange={e => up('orders_limit', parseInt(e.target.value) || 0)} className={inputCls} data-testid="plan-orders-limit" />
            </Field>
            <Field label="Leads limit" hint="-1 = unlimited">
              <input type="number" value={form.leads_limit} onChange={e => up('leads_limit', parseInt(e.target.value) || 0)} className={inputCls} />
            </Field>
            <Field label="Users limit">
              <input type="number" value={form.users_limit} onChange={e => up('users_limit', parseInt(e.target.value) || 0)} className={inputCls} />
            </Field>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Extra order price" hint="USD per order past limit">
              <input type="number" step="0.01" value={form.extra_order_price} onChange={e => up('extra_order_price', parseFloat(e.target.value) || 0)} className={inputCls} />
            </Field>
            <Field label="Extra user price" hint="USD/month per extra seat">
              <input type="number" step="0.01" value={form.extra_user_price} onChange={e => up('extra_user_price', parseFloat(e.target.value) || 0)} className={inputCls} />
            </Field>
          </div>

          {/* Features */}
          <div>
            <label className="block text-xs uppercase tracking-wider text-slate-400 mb-2">Features (pricing page bullets)</label>
            <div className="space-y-2">
              {(form.features || []).map((f, i) => (
                <div key={i} className="flex gap-2">
                  <input value={f} onChange={e => updateFeature(i, e.target.value)} className={inputCls} placeholder="5,000 orders/month" />
                  <button onClick={() => removeFeature(i)} className="px-3 rounded-lg bg-slate-800 hover:bg-rose-500/20 text-rose-400"><X className="w-4 h-4" /></button>
                </div>
              ))}
              <button onClick={addFeature} className="text-xs text-amber-400 hover:text-amber-300"><Plus className="w-3 h-3 inline" /> Add feature</button>
            </div>
          </div>

          {/* Modules */}
          <div>
            <label className="block text-xs uppercase tracking-wider text-slate-400 mb-2">Modules unlocked by this plan</label>
            <p className="text-xs text-slate-500 mb-2">These app sections are visible/usable for companies on this plan.</p>
            <div className="space-y-3">
              {Object.entries(moduleGroups).map(([g, mods]) => (
                <div key={g} className="bg-slate-950 border border-slate-800 rounded-lg p-3">
                  <div className="text-xs uppercase tracking-wider text-slate-500 mb-2">{g}</div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {mods.map(m => {
                      const on = form.modules_enabled.includes(m.key);
                      return (
                        <label key={m.key} className={`flex items-center gap-2 px-2 py-1.5 rounded cursor-pointer text-sm ${on ? 'bg-amber-500/10 text-amber-300' : 'text-slate-400 hover:bg-slate-800'} ${m.locked ? 'opacity-60' : ''}`}>
                          <input
                            type="checkbox" checked={on}
                            disabled={m.locked}
                            onChange={() => !m.locked && toggleModule(m.key)}
                            className="accent-amber-500"
                            data-testid={`module-toggle-${m.key}`}
                          />
                          {m.label}
                        </label>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Integrations — checkbox grid auto-populated from INTEGRATION_REGISTRY */}
          <div>
            <label className="block text-xs uppercase tracking-wider text-slate-400 mb-2">Integrations unlocked by this plan</label>
            <p className="text-xs text-slate-500 mb-2">
              Third-party services tenants can connect on this plan. New integrations added to the
              app appear here automatically — no code changes required.
            </p>

            {/* "Grant all" toggle — saves the wildcard `*` so future
                integrations are auto-granted to this plan. The
                IntegrationsPage UI + backend gate both honour this so an
                Enterprise-style plan never has to chase new keys. */}
            {(() => {
              const hasWildcard = (form.integrations || []).includes('*');
              return (
                <label
                  className={`flex items-center gap-2 px-3 py-2 mb-2 rounded cursor-pointer text-sm border ${
                    hasWildcard
                      ? 'bg-amber-500/15 border-amber-500/40 text-amber-200'
                      : 'border-slate-800 text-slate-300 hover:bg-slate-800'
                  }`}
                  data-testid="integration-toggle-wildcard"
                >
                  <input
                    type="checkbox"
                    checked={hasWildcard}
                    onChange={() => up('integrations', hasWildcard ? [] : ['*'])}
                    className="accent-amber-500"
                  />
                  <span className="font-semibold">Grant all integrations (★)</span>
                  <span className="text-xs text-slate-500 ml-auto">
                    Includes any integration we add in the future
                  </span>
                </label>
              );
            })()}
            
            {(form.integrations || []).includes('*') ? (
              <div className="text-xs text-slate-500 italic px-2">
                Wildcard active — all integrations (current and future) are unlocked on this plan.
                Uncheck the box above to pick individual integrations instead.
              </div>
            ) : Object.keys(integrationGroups).length === 0 ? (
              <div className="text-xs text-slate-500 italic">No integrations registered yet.</div>
            ) : (
              <div className="space-y-3">
                {Object.entries(integrationGroups).map(([g, items]) => (
                  <div key={g} className="bg-slate-950 border border-slate-800 rounded-lg p-3">
                    <div className="text-xs uppercase tracking-wider text-slate-500 mb-2">{g}</div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                      {items.map(i => {
                        const on = (form.integrations || []).includes(i.key);
                        return (
                          <label key={i.key} className={`flex items-center gap-2 px-2 py-1.5 rounded cursor-pointer text-sm ${on ? 'bg-amber-500/10 text-amber-300' : 'text-slate-400 hover:bg-slate-800'}`}>
                            <input
                              type="checkbox"
                              checked={on}
                              onChange={() => toggleIntegration(i.key)}
                              className="accent-amber-500"
                              data-testid={`integration-toggle-${i.key}`}
                            />
                            {i.label}
                          </label>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Stripe price ID">
              <input value={form.stripe_price_id || ''} onChange={e => up('stripe_price_id', e.target.value)} className={inputCls} placeholder="price_xxx" />
            </Field>
            <Field label="Shopify plan name">
              <input value={form.shopify_plan_name || ''} onChange={e => up('shopify_plan_name', e.target.value)} className={inputCls} placeholder="Pro" />
            </Field>
          </div>

          <div className="flex gap-4 items-center">
            <label className="flex items-center gap-2 text-sm text-slate-300 cursor-pointer">
              <input type="checkbox" checked={form.is_active} onChange={e => up('is_active', e.target.checked)} className="accent-amber-500" data-testid="plan-is-active" />
              Active (shown on pricing page)
            </label>
            <label className="flex items-center gap-2 text-sm text-slate-300 cursor-pointer">
              <input type="checkbox" checked={form.is_default} onChange={e => up('is_default', e.target.checked)} className="accent-amber-500" />
              Default plan (new signups)
            </label>
          </div>

          <div>
            <button onClick={() => setShowJson(v => !v)} className="text-xs text-slate-500 inline-flex items-center gap-1">
              {showJson ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
              Raw JSON
            </button>
            {showJson && (
              <pre className="mt-2 bg-slate-950 border border-slate-800 rounded-lg p-3 text-xs text-slate-300 font-mono overflow-auto max-h-48">
{JSON.stringify(form, null, 2)}
              </pre>
            )}
          </div>
        </div>

        <footer className="px-6 py-4 border-t border-slate-800 flex items-center justify-end gap-2 sticky bottom-0 bg-slate-900">
          <button onClick={() => onClose(false)} className="px-4 py-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-200 text-sm">Cancel</button>
          <button onClick={save} disabled={saving} className="px-4 py-2 rounded-lg bg-amber-500 hover:bg-amber-400 text-black text-sm font-medium inline-flex items-center gap-2 disabled:opacity-50" data-testid="plan-save-btn">
            <Save className="w-4 h-4" /> {saving ? 'Saving…' : 'Save plan'}
          </button>
        </footer>
      </div>
    </div>
  );
};

const inputCls = 'w-full px-3 py-2 rounded-lg bg-slate-950 border border-slate-800 text-white text-sm focus:outline-none focus:border-amber-500/50';
const Field = ({ label, hint, children }) => (
  <div>
    <label className="block text-xs uppercase tracking-wider text-slate-400 mb-1">{label}</label>
    {children}
    {hint && <p className="text-[10px] text-slate-500 mt-1">{hint}</p>}
  </div>
);

export default SuperPlansPage;
